import { onBeforeUnmount, onMounted, ref } from "vue";
import {
  notificationApi,
  type NotificationCategory,
  type NotificationView,
} from "@/api/notification.api";

const POLL_INTERVAL_MS = 30_000;
const DROPDOWN_LIMIT = 20;

// Module-scoped state so the bell badge + dropdown + full-page inbox share
// one source of truth across components.
const items = ref<NotificationView[]>([]);
const unreadCount = ref(0);
const hasMore = ref(false);
const loading = ref(false);
const error = ref<string | null>(null);

let pollTimer: ReturnType<typeof setInterval> | null = null;
let focusHandler: (() => void) | null = null;
let visibilityHandler: (() => void) | null = null;
let activeMounts = 0;

async function refreshCount(): Promise<void> {
  try {
    const { count } = await notificationApi.countUnread();
    unreadCount.value = count;
  } catch (e) {
    error.value = (e as Error).message;
  }
}

async function refreshList(
  opts: {
    categories?: NotificationCategory[];
    unreadOnly?: boolean;
    limit?: number;
  } = {},
): Promise<void> {
  loading.value = true;
  try {
    const res = await notificationApi.list({
      categories: opts.categories,
      unreadOnly: opts.unreadOnly,
      limit: opts.limit ?? DROPDOWN_LIMIT,
    });
    items.value = res.items;
    hasMore.value = res.hasMore;
    // Keep count consistent with the freshly returned set when filter is "all + not unreadOnly".
    if (!opts.categories?.length && !opts.unreadOnly) {
      unreadCount.value = res.items.filter((n) => !n.readAt).length;
    } else {
      // For filtered views the badge needs an authoritative count.
      await refreshCount();
    }
  } catch (e) {
    error.value = (e as Error).message;
  } finally {
    loading.value = false;
  }
}

async function loadMore(
  opts: {
    categories?: NotificationCategory[];
    unreadOnly?: boolean;
    limit?: number;
  } = {},
): Promise<void> {
  if (!hasMore.value || items.value.length === 0) return;
  const last = items.value[items.value.length - 1];
  loading.value = true;
  try {
    const res = await notificationApi.list({
      categories: opts.categories,
      unreadOnly: opts.unreadOnly,
      before: last.id,
      limit: opts.limit ?? DROPDOWN_LIMIT,
    });
    items.value = [...items.value, ...res.items];
    hasMore.value = res.hasMore;
  } catch (e) {
    error.value = (e as Error).message;
  } finally {
    loading.value = false;
  }
}

function startPolling(): void {
  if (pollTimer) return;
  pollTimer = setInterval(() => {
    if (document.visibilityState === "visible") refreshCount();
  }, POLL_INTERVAL_MS);

  focusHandler = () => refreshCount();
  visibilityHandler = () => {
    if (document.visibilityState === "visible") refreshCount();
  };
  window.addEventListener("focus", focusHandler);
  document.addEventListener("visibilitychange", visibilityHandler);
}

function stopPolling(): void {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  if (focusHandler) {
    window.removeEventListener("focus", focusHandler);
    focusHandler = null;
  }
  if (visibilityHandler) {
    document.removeEventListener("visibilitychange", visibilityHandler);
    visibilityHandler = null;
  }
}

/**
 * Reactive notification badge + inbox list. Multiple components may call
 * this; state is module-scoped so only the FIRST mount kicks off polling
 * and the LAST unmount tears it down.
 *
 * - `unreadCount` refreshed every 30s while the tab is visible + on focus.
 * - `items` refreshed on demand (when dropdown opens / page mounts).
 * - `loadMore()` appends older rows using the cursor from the last item.
 */
export function useNotifications() {
  onMounted(async () => {
    activeMounts++;
    if (activeMounts === 1) {
      startPolling();
      await refreshCount();
    }
  });

  onBeforeUnmount(() => {
    activeMounts--;
    if (activeMounts === 0) stopPolling();
  });

  async function markRead(id: string): Promise<void> {
    await notificationApi.markRead(id);
    const item = items.value.find((n) => n.id === id);
    if (item && !item.readAt) {
      item.readAt = new Date().toISOString();
      if (unreadCount.value > 0) unreadCount.value--;
    }
  }

  async function markAllRead(): Promise<void> {
    await notificationApi.markAllRead();
    for (const n of items.value) {
      if (!n.readAt) n.readAt = new Date().toISOString();
    }
    unreadCount.value = 0;
  }

  async function dismiss(id: string): Promise<void> {
    await notificationApi.dismiss(id);
    const idx = items.value.findIndex((n) => n.id === id);
    if (idx !== -1) {
      const wasUnread = !items.value[idx].readAt;
      items.value.splice(idx, 1);
      if (wasUnread && unreadCount.value > 0) unreadCount.value--;
    }
  }

  // Note: refs are returned mutable (not wrapped in readonly()) because
  // template v-for + click handlers pass NotificationView through, and the
  // DeepReadonly type tripped Cloudflare Pages' stricter tsc. Consumers
  // are still expected to mutate via the helpers (markRead / markAllRead /
  // dismiss) rather than poking the ref directly.
  return {
    items,
    unreadCount,
    hasMore,
    loading,
    error,
    refreshList,
    refreshCount,
    loadMore,
    markRead,
    markAllRead,
    dismiss,
  };
}
